import React, { useState, useEffect } from 'react';
import { useFormContext } from '../../context/FormContext';
import { HomeFeatures } from '../../types';

const KITCHEN_OPTIONS = [
  'Stainless Steel Appliances',
  'Gas Range',
  'Electric Range',
  'Wine Cooler',
  'Island',
  'Eat-In-Kitchen',
  'Granite Countertops',
  'Marble Countertops',
  'Quartz Countertops',
  'Pantry',
];

const FLOORING_OPTIONS = [
  'Hardwood',
  'Laminate',
  'Tile',
  'Carpet',
  'Vinyl',
];

const OUTBUILDING_OPTIONS = [
  'Barn',
  'Gazebo',
  'Garage Apartment',
  'Guest House',
  'Shed',
  'Boathouse',
  'Staff Quarters',
  'Green House',
  'Pool House',
  'Stable',
];

const OTHER_OPTIONS = [
  'Fenced Yard',
  'Hot Tub',
  'Sprinkler System',
  'Security System',
  'Built-In Sound System',
  'Pool Heater',
  'Separate Laundry Room',
  'Home Owners Association',
  'Cul-de-sac',
  'Corner Property',
  'Conservation Land',
  'Elevator',
  'Deck',
  'Balcony',
  'Putting Green',
  'Sauna',
  'Auto Garage Door',
  'Boat Slip',
  'Dock',
  'Generator',
  'Intercom',
  'Screened Porch',
];

function CheckboxGroup({
  options,
  selected,
  onChange,
}: {
  options: string[];
  selected: string[];
  onChange: (updated: string[]) => void;
}) {
  const toggle = (item: string, checked: boolean) => {
    onChange(checked ? [...selected, item] : selected.filter((s) => s !== item));
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((option) => (
        <label key={option} className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={selected.includes(option)}
            onChange={(e) => toggle(option, e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm text-gray-700">{option}</span>
        </label>
      ))}
    </div>
  );
}

export default function Section7HomeFeatures() {
  const { state, saveSection } = useFormContext();
  const [data, setData] = useState<HomeFeatures>({
    intake_id: '',
  });

  useEffect(() => {
    // Only initialize once — guard prevents re-running when formData updates after saves
    if (data.intake_id) return;
    if (state.formData.home_features) {
      setData(state.formData.home_features);
    } else if (state.intake) {
      setData(prev => ({ ...prev, intake_id: state.intake!.id }));
    }
  }, [state.formData.home_features, state.intake]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state.intake && data.intake_id) {
      saveSection('home_features', data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, state.intake]);

  const updateField = (field: keyof HomeFeatures, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const getList = (field: string): string[] => {
    const val = (data as any)[field];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val) return val.split(', ');
    return [];
  };

  const setList = (listField: string, storageField: string, updated: string[]) => {
    setData(prev => ({
      ...prev,
      [listField]: updated,
      [storageField]: updated.join(', '),
    }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Home Features</h2>
        <p className="text-sm text-gray-500 mb-6">Please check all that apply</p>

        <div className="space-y-8">
          {/* Kitchen */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-3 uppercase tracking-wide">Kitchen</h3>
            <CheckboxGroup
              options={KITCHEN_OPTIONS}
              selected={getList('kitchen_features_list')}
              onChange={(updated) => setList('kitchen_features_list', 'kitchen_features', updated)}
            />
          </div>

          {/* Flooring */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-3 uppercase tracking-wide">Flooring</h3>
            <CheckboxGroup
              options={FLOORING_OPTIONS}
              selected={getList('flooring_list')}
              onChange={(updated) => setList('flooring_list', 'flooring', updated)}
            />
          </div>

          {/* Outbuildings */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-3 uppercase tracking-wide">Outbuildings</h3>
            <CheckboxGroup
              options={OUTBUILDING_OPTIONS}
              selected={getList('outbuildings_list')}
              onChange={(updated) => setList('outbuildings_list', 'outbuildings', updated)}
            />
          </div>

          {/* Other */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-3 uppercase tracking-wide">Other</h3>
            <CheckboxGroup
              options={OTHER_OPTIONS}
              selected={getList('other_amenities_list')}
              onChange={(updated) => setList('other_amenities_list', 'other_amenities', updated)}
            />
          </div>

          {/* Other Features (free text) */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-3 uppercase tracking-wide">Other Features</h3>
            <textarea
              value={data.other_features || ''}
              onChange={(e) => updateField('other_features', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              rows={4}
              placeholder="Any other features, upgrades, or special characteristics that make your home unique"
            />
          </div>
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            Think about what makes your home special! These features help buyers imagine themselves
            living in your space.
          </p>
        </div>
      </div>
    </div>
  );
}
